import React from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { useOverviewDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/store/overviewDashboard.store';
import { useChartColors } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { SHIFT_BREAKDOWN_COLOR_INDICES } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.constants';
import {
  DonutChart,
  DonutChartItem,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/DonutChart';
import {
  EmptyStateContainer,
  DashboardErrorContainer,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import {
  ChartContainer,
  SvgContainer,
  DonutCenter,
  DonutCenterValue,
  LegendContainer,
  LegendItem,
  LegendDot,
  LegendLabel,
  LegendPercentage,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/ShiftBreakdownChart.styled';

function getColorByIndex(
  idx: number,
  chartColors: string[],
  thresholds: { critical: string; attention: string },
): string {
  const indices = Object.values(SHIFT_BREAKDOWN_COLOR_INDICES);
  const colorIndex = indices[idx] ?? idx;
  if (colorIndex === -1) return thresholds.critical;
  if (colorIndex === -2) return thresholds.attention;
  return chartColors[colorIndex % chartColors.length];
}

export const ShiftBreakdownChart: React.FC = () => {
  const { t } = useTranslation();
  const shiftBreakdown = useOverviewDashboardStore((s) => s.shiftBreakdown);
  const totalShifts = useOverviewDashboardStore((s) => s.totalShifts);
  const error = useOverviewDashboardStore((s) => s.breakdownError);
  const palette = useChartColors();

  if (error) {
    return <DashboardErrorContainer>{t('reports:DASHBOARD_LOAD_ERROR')}</DashboardErrorContainer>;
  }

  const hasData = shiftBreakdown.length > 0;

  if (!hasData) {
    return (
      <EmptyStateContainer>No shift data available for the selected filters.</EmptyStateContainer>
    );
  }

  const items: DonutChartItem[] = shiftBreakdown.map((item, idx) => {
    const label = t(item.category);
    const color = getColorByIndex(idx, palette.chartColors, palette.threshold);
    const percentage = formatDashboardNumber(item.percentage);
    return {
      label,
      value: item.count,
      color,
      ariaLabel: `${label}: ${item.count} shifts (${percentage}%)`,
      tooltipHtml: `${label}: ${item.count.toLocaleString()} (${percentage}%)`,
    };
  });

  return (
    <ChartContainer aria-label={`Shift breakdown: ${totalShifts.toLocaleString()} total shifts`}>
      <SvgContainer>
        <DonutChart items={items} size={480} />
        <DonutCenter>
          <DonutCenterValue>{totalShifts.toLocaleString()}</DonutCenterValue>
        </DonutCenter>
      </SvgContainer>
      <LegendContainer>
        {shiftBreakdown.map((item, idx) => (
          <LegendItem key={item.category}>
            <LegendDot $color={getColorByIndex(idx, palette.chartColors, palette.threshold)} />
            <LegendLabel>{t(item.category)}</LegendLabel>
            <LegendPercentage>{formatDashboardNumber(item.percentage)}%</LegendPercentage>
          </LegendItem>
        ))}
      </LegendContainer>
    </ChartContainer>
  );
};
